import React, { useRef, useState } from 'react';
import { Upload, Trash2, Image as ImageIcon, Film, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadMedia } from '../../lib/api';

/**
 * MediaUpload — uploads image / GIF / video / audio to the backend and stores
 * a server URL (small) in the parent state, NOT a base64 string. This keeps
 * the site-config payload tiny so large videos can be saved without hitting
 * proxy body-size limits.
 *
 * props:
 *   value         — URL string (relative `/api/uploads/<id>.<ext>` or absolute http)
 *   onChange(v)   — callback; '' means cleared
 *   accept        — 'image' | 'media' (image+video+audio). default 'media'
 *   maxFileMB     — hard cap (default 50 — backend enforces too)
 *   previewClass  — Tailwind classes for preview box (default 'h-16 w-28')
 *   shape         — 'rect' | 'square' | 'circle' (default 'rect')
 *   testid        — base data-testid
 */
export default function MediaUpload({
  value, onChange, accept = 'media',
  maxFileMB = 50,
  previewClass = 'h-16 w-28', shape = 'rect',
  testid = 'media-upload'
}) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const acceptAttr = accept === 'image' ? 'image/*' : 'image/*,video/*,image/gif';

  const handle = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > maxFileMB * 1024 * 1024) {
      return toast.error(`File too large. Max ${maxFileMB} MB.`);
    }
    setBusy(true); setProgress(0);
    try {
      const res = await uploadMedia(f, (p) => setProgress(p));
      onChange(res.url); // store server URL
      toast.success(`Uploaded (${(res.size / 1024).toFixed(0)} KB)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); setProgress(0); }
  };

  const shapeCls = shape === 'circle' ? 'rounded-full' : shape === 'square' ? 'rounded-md aspect-square' : 'rounded-md';

  // Resolve URL for preview: relative API URLs need the backend prefix
  const resolved = resolveMediaUrl(value);
  const isVideo = isVideoSrc(value);

  return (
    <div className="flex items-center gap-2">
      <div className={`${previewClass} ${shapeCls} border border-zinc-800 bg-zinc-900 overflow-hidden flex items-center justify-center shrink-0 relative`}>
        {value ? (
          isVideo ? (
            <video src={resolved} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          ) : (
            <img src={resolved} alt="preview" className="w-full h-full object-cover" />
          )
        ) : (
          <span className="text-zinc-600 text-[10px] flex flex-col items-center gap-1">
            {accept === 'image' ? <ImageIcon size={14} /> : <Film size={14} />}
            <span>{accept === 'image' ? 'no image' : 'no media'}</span>
          </span>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-[10px] text-white font-bold">
            {progress > 0 ? `${progress}%` : '…'}
          </div>
        )}
      </div>
      <input ref={ref} type="file" accept={acceptAttr} onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="h-9 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-sm flex items-center gap-1.5 disabled:opacity-60" data-testid={`${testid}-btn`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? `Uploading…` : 'Upload'}
      </button>
      {value && !busy && (
        <button type="button" onClick={() => onChange('')} className="h-9 px-2 rounded-md bg-red-600/15 hover:bg-red-600/25 border border-red-900/40 text-red-300" data-testid={`${testid}-clear`} aria-label="Remove media">
          <Trash2 size={14} />
        </button>
      )}
      {value && !busy && (
        <span className="text-[10px] text-zinc-500 hidden md:inline-flex items-center gap-1 pl-1">
          {isVideo ? <><Film size={11} /> Video</> : <><ImageIcon size={11} /> {value.endsWith('.gif') ? 'GIF' : 'Image'}</>}
        </span>
      )}
    </div>
  );
}

// ===== Helpers =====
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export function resolveMediaUrl(v) {
  if (!v) return '';
  if (typeof v !== 'string') return '';
  if (v.startsWith('data:') || v.startsWith('http')) return v;
  if (v.startsWith('/api/')) return `${BACKEND_URL}${v}`;
  return v;
}

export function isVideoSrc(v) {
  if (typeof v !== 'string') return false;
  if (v.startsWith('data:video')) return true;
  if (/(\.mp4|\.webm|\.mov|\.m4v|\.ogv)(\?|$)/i.test(v)) return true;
  return false;
}
